import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../../i18n/config';
import './language-selector-circle.css';

/**
 * Selector de idiomas circular con dropdown
 *
 * @remarks
 * Botón circular con el código del idioma actual (ES, EN, PT, FR).
 * Al hacer click, despliega un dropdown con todos los idiomas disponibles.
 * Mismo estilo que UserAvatar (40px, borde cyan, glassmorphism).
 * SIN emojis de banderas.
 *
 * @example
 * ```tsx
 * <LanguageSelectorCircle />
 * ```
 */
export function LanguageSelectorCircle(): JSX.Element {
  const { i18n, t } = useTranslation('admin');
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  /**
   * Obtiene el idioma actual
   */
  const currentLanguage = LANGUAGES.find(lang => lang.code === i18n.language) || LANGUAGES[0];

  /**
   * Toggle del menú desplegable
   */
  const toggleMenu = (): void => {
    setIsOpen(!isOpen);
  };

  /**
   * Maneja el cambio de idioma
   */
  const handleLanguageChange = (langCode: string): void => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('wayline_language', langCode);
    setIsOpen(false);
  };

  /**
   * Hook para cerrar el menú al hacer click fuera
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        menuRef.current &&
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="language-selector-circle-container">
      {/* Botón circular con código de idioma */}
      <button
        ref={buttonRef}
        className="language-selector-circle"
        onClick={toggleMenu}
        aria-label={t('language.select')}
        aria-expanded={isOpen}
        aria-haspopup="true"
        title={`${t('language.label')}: ${currentLanguage.nativeName}`}
      >
        <div className="lang-circle">{currentLanguage.code.toUpperCase()}</div>
      </button>

      {/* Dropdown menu (condicional) */}
      {isOpen && (
        <div className="language-menu" ref={menuRef} role="menu">
          <div className="language-menu-header">{t('language.label')}</div>
          <div className="language-menu-divider" />
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              className={`language-menu-item ${i18n.language === lang.code ? 'active' : ''}`}
              onClick={() => handleLanguageChange(lang.code)}
              role="menuitem"
            >
              <span className="lang-name">{lang.nativeName}</span>
              <span className="lang-code-small">{lang.code.toUpperCase()}</span>
              {i18n.language === lang.code && (
                <span className="lang-check">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
